import { ACTIVITY_TYPES } from './activity_types'
import { ACTIVITYGROUP_MODES } from './activityGroupModes'
import { appendQueryParams } from './url_helper'
import colors from '../pages/Activity/colors' // colors used in getColor below

// Derive a color for an activity group / person context.
// Priority: explicit activityGroup.colorTheme -> gender based default -> blue fallback
export const getColor = ({ person, activityGroup } = {}) => {
  const theme = activityGroup?.colorTheme
  if (theme && colors?.[theme]) return colors[theme]
  if (person) {
    if (person?.gender === 'MALE') return colors.blue
    return colors.red
  }
  return colors.blue
}

export const getCompletedCounts = ({ visibleId, personId, answerCounts }) => {
  if (!answerCounts || !visibleId) return ''

  const match = answerCounts?.find(
    (answer) => answer?.visibleId === visibleId && (personId ? answer?.personId === personId : true)
  )
  return match?.count ?? ''
}

export const enableActivityEdit = ({ topicId, dataType, activityGroupId, hasCompleteDate }) => {
  const editableTypes = ['question-answer', 'bucket-list']
  const personalJournalEditConditions = hasCompleteDate && activityGroupId === 'personal-journal'
  const canEdit = topicId === 'stories' && hasCompleteDate && !!editableTypes.find((dt) => dt === dataType)
  return canEdit || !!personalJournalEditConditions
}

export const enableActivityShare = ({ shareable, hasCompleteDate }) => {
  const canShare = shareable && hasCompleteDate
  return canShare
}

export const getMemoryEditUrl = (storyMemoryArtifactId) => {
  return storyMemoryArtifactId ? `/memories/memory/${storyMemoryArtifactId}` : null
}

export const getCarouselFocusUpdates = ({ activeIndex, slides }) => {
  const focusableElementsSelector = [
    'a[href]',
    'button',
    'input',
    'textarea',
    'select',
    'details',
    '[tabindex]:not([tabindex="-1"])',
    '[data-interactive="true"]',
    'fieldset',
    '[role="switch"]',
  ].join(', ')

  return slides
    .map((slide, index) => {
      const focusableElements = Array.from(slide.querySelectorAll(focusableElementsSelector))
      return focusableElements.map((el) => ({
        element: el,
        newTabIndex: activeIndex === index ? '0' : '-1',
      }))
    })
    .flat()
}

export const populateActivityGroupUrl = ({ splash, link, topicId, visibleId, familyGroup, personId }) => {
  const activityGroupUrl = `/${topicId}/${visibleId}`
  const activityGroupWithFamilyUrl = `${activityGroupUrl}/${familyGroup?.familyGroupId}`

  let toUrl

  if (link) {
    toUrl = link
  } else if (familyGroup) {
    if (familyGroup.isGroup || familyGroup?.familyGroupId === 'ALL') toUrl = `${activityGroupWithFamilyUrl}`
    else toUrl = `${activityGroupWithFamilyUrl}/${familyGroup?.cisId?.split('cis.user.')[1]}`
  } else toUrl = activityGroupUrl

  if (splash !== 'external' && personId) {
    toUrl = appendQueryParams(toUrl, { personId })
  }

  return toUrl
}

export const prependNameOnAnswers = ({ familyGroup, answers }) => {
  const expandedAnswers = answers?.map((answer) => {
    return {
      ...answer,
      subtitlePrefix: familyGroup?.persons?.find((person) => person?.memberCisId === answer?.cisUserId)
        ?.userProfileName,
    }
  })
  return expandedAnswers
}

export const filterAnswersByCisId = (answers, cisUserId) => {
  return answers?.length > 0 && cisUserId ? answers.filter((answer) => answer?.cisUserId === cisUserId) : answers
}

export const filterAnswersByCisIds = (answers, persons) => {
  if (answers?.length > 0) {
    return persons?.length > 0
      ? answers.filter((answer) => persons?.some((person) => person?.memberCisId === answer?.cisUserId)) // return answers for people in the FamilyGroup
      : answers
  }
  return []
}

export const filterAnswersByFamilyGroupSelection = (answers, familyGroup) => {
  if (answers?.length > 0) {
    if (familyGroup?.cisId && !familyGroup?.isGroup) return filterAnswersByCisId(answers, familyGroup?.cisId)
    if (!familyGroup?.cisId && familyGroup?.persons) return filterAnswersByCisIds(answers, familyGroup?.persons)
    return answers
  }
  return []
}

const filterAnswersByActivityGroupIdsId = (answers, activityGroupId) => {
  return answers?.length > 0 && activityGroupId
    ? answers.filter((answer) => answer?.activityGroupId === activityGroupId)
    : answers
}

export const getFilteredCategoryItems = ({ categories, answers, returnAnswered }) => {
  const categoriesCopy = JSON.parse(JSON.stringify(categories))
  return categoriesCopy?.reduce((result, category) => {
    category.activityGroups = category?.activityGroups?.filter((activityGroup) => {
      const agAnswers = filterAnswersByActivityGroupIdsId(answers, activityGroup?.activityGroupId)
      activityGroup.count = returnAnswered ? agAnswers?.length : null
      activityGroup.answers = agAnswers
      return agAnswers?.length > 0
    })

    if (category?.activityGroups.length > 0) result.push(category)
    return result
  }, [])
}

const populateGeneralAnswer = (activity, answers) => {
  const answer = answers?.find(
    (savedActivityGroupAnswer) => savedActivityGroupAnswer?.activityId === activity?.activityId
  )
  return answer
}

const populateRankingAnswer = (activity, rankings) => {
  let answer
  if (rankings?.length > 0) {
    answer = rankings?.filter(
      (savedActivityGroupAnswer) => savedActivityGroupAnswer?.activityId === activity.activityId
    )
    answer?.forEach((option) => {
      option.title = activity.options.find((searchOpt) => searchOpt.optionId === option.optionId)?.title
    })
    answer = answer.sort((a, b) => a.rank - b.rank)
  }
  return answer
}

const populateSingleSelectAnswer = (activity, selections) => {
  let answer
  if (selections?.length > 0) {
    answer = selections?.find(
      (savedActivityGroupAnswer) => savedActivityGroupAnswer?.activityId === activity?.activityId
    )

    const matchingRawOption = activity?.options?.find((opt) => opt?.optionId === answer?.optionId)
    answer = { ...answer, ...matchingRawOption }
  }
  return answer
}

const populateSavedActivityAnswers = (savedActivities, selectedSavedActivityGroupAnswers) => {
  const savedActivitiesWithAnswers = savedActivities?.map((activity) => {
    let savedAnswer
    switch (activity?.type ?? activity?.dataType) {
      case ACTIVITY_TYPES.SELECT_SINGLE:
      case 'selection':
        savedAnswer = populateSingleSelectAnswer(activity, selectedSavedActivityGroupAnswers?.selections)
        break
      case ACTIVITY_TYPES.RANKING:
        savedAnswer = populateRankingAnswer(activity, selectedSavedActivityGroupAnswers?.rankings)
        break
      case ACTIVITY_TYPES.QUESTION_ANSWER:
      case ACTIVITY_TYPES.QUESTION_ANSWER_PHOTOS:
        savedAnswer = populateGeneralAnswer(activity, selectedSavedActivityGroupAnswers?.conversations)
        break
      case ACTIVITY_TYPES.FORM_FIELD:
        savedAnswer = populateGeneralAnswer(activity, selectedSavedActivityGroupAnswers?.formFields)
        break
      default:
        break
    }

    return { ...activity, savedAnswer, answerSetTitle: selectedSavedActivityGroupAnswers?.title ?? '' }
  })

  return savedActivitiesWithAnswers
}

const getActivitiesWithAnswers = ({ activities, selectedSavedActivityGroupAnswers }) => {
  // Collect answers from all supported properties (some answer groups contain multiple types simultaneously)
  const possibleActivityListProperties = ['selections', 'rankings', 'options', 'conversations', 'formFields', 'top10']
  const allAnswerItems = possibleActivityListProperties.reduce((acc, prop) => {
    const items = selectedSavedActivityGroupAnswers?.[prop]
    if (items?.length) acc.push(...items)
    return acc
  }, [])
  if (!allAnswerItems.length) return []
  const answeredIds = new Set(allAnswerItems.map((a) => a?.activityId))
  const matchingActivities = activities?.filter((act) => answeredIds.has(act?.activityId))
  const answeredActivities = populateSavedActivityAnswers(matchingActivities, selectedSavedActivityGroupAnswers)
  return answeredActivities
}

const getPresentationMode = ({ activityGroupMode, dataType }) => {
  let mode
  if (activityGroupMode === ACTIVITYGROUP_MODES.REVIEW) {
    if (dataType === ACTIVITY_TYPES.QUESTION_ANSWER || dataType === 'bucket-list') {
      mode = 'STORY_REVIEW'
    } else if (dataType === ACTIVITY_TYPES.FORM_FIELD) {
      mode = 'FORM_FIELD_REVIEW'
    } else if (dataType === 'top-10') {
      mode = 'TOP-10-INPUT_REVIEW'
    } else if (dataType === 'selection') {
      mode = 'SINGLE_SELECT_REVIEW'
    } else {
      mode = 'TDO'
    }
  } else {
    mode = 'TDO'
  }

  return mode
}

const updateAnswerInArray = (answers, newAnswer) => {
  const updatedAnswers = [...answers]
  const foundIndex = updatedAnswers.findIndex((x) => {
    return (
      (x?.id && x?.id === newAnswer?.id) ||
      (x?.activityId && x?.activityId === newAnswer?.activityId) ||
      (x?.title && x?.title === newAnswer?.title) ||
      (x?.optionId && x?.optionId === newAnswer?.optionId)
    )
  })

  if (foundIndex > -1) {
    if (newAnswer?.delete) updatedAnswers.splice(foundIndex, 1)
    else updatedAnswers[foundIndex] = newAnswer
  } else if (!newAnswer?.delete) updatedAnswers.push(newAnswer)
  return updatedAnswers
}

export const populateRelevantDates = (answers) =>
  answers.map((answer) => {
    const { eventDate, datePlace, completeDate } = answer
    const relevantDate = (datePlace && datePlace?.dateNormalizedText) || eventDate || completeDate // TO DO: Remove checking for eventDate after backend is updated
    return relevantDate ? { ...answer, relevantDate } : answer
  })

const sortAnswers = ({ answers, familyGroupCisId }) => {
  if (!answers) return []

  const filtered = familyGroupCisId ? answers.filter((item) => item?.cisUserId === familyGroupCisId) : answers

  let unfinishedAnswers = filtered.filter((item) => !item.completeDate)
  unfinishedAnswers = unfinishedAnswers.sort((a, b) => new Date(b.createDate) - new Date(a.createDate))

  let finishedAnswers = filtered.filter((item) => item.completeDate)

  finishedAnswers = finishedAnswers.sort(
    (a, b) => new Date(b.relevantDate ?? b.relevantDate) - new Date(a.relevantDate ?? a.relevantDate)
  )

  return [...unfinishedAnswers, ...finishedAnswers]
}

export { getPresentationMode, getActivitiesWithAnswers, updateAnswerInArray, sortAnswers }
